import React from "react";
import PropTypes from "prop-types";
import {
	Container,
	Box,
	Stack,
	Typography,
	Card,
	CardContent,
	CardActions,
	Switch,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	IconButton,
	Button,
	TextField
} from "@mui/material";

import { ConfigContext } from "../Config/ConfigContext";
import { capitalize, debounce, set } from "lodash";
import { Add, Delete } from "@mui/icons-material";
import EnvironmentDeleteModal from "./environmentDeleteModal";
import EnvironmentURIAddModal from "./environmentURIAddModal";
import EnvironmentURIDeleteModal from "./environmentURIDeleteModal";
import EnvironmentDomainAddModal from "./environmentDomainAddModal";
import EnvironmentDomainDeleteModal from "./environmentDomainDeleteModal";
import useApi from "../hooks/useApi";

/**
 * Loads the environment detail from the API.
 *
 * @param {Object} param0 - The parameter object.
 * @param {Object} param0.values - The current state values.
 * @param {Object} param0.api - The API hook instance.
 * @returns {Promise<Object>} A promise that resolves to the updated values.
 */
const onLoad = async ({ values, api }) => {
	const environment = await api.environment.getById({ id: values._id });
	return {
		...values,
		...environment,
		loading: false
	};
};

/**
 * Saves data to the API using a debounce delay.
 *
 * @param {Object} data - The data to be saved.
 * @param {Object} api - The API hook instance.
 * @param {string|number} environmentId - The environment ID.
 * @returns {Promise<void>} A promise that resolves when the save operation completes.
 */
const saveField = debounce(async (data, api, environmentId) => {
	await api.environment.update({ id: environmentId, data: data });
}, 500);

/**
 * EnvironmentDetail component.
 *
 * @component
 * @param {Object} props - Component props.
 * @param {string|number} props.environmentId - The id of the environment.
 * @returns {JSX.Element} The rendered EnvironmentDetail component.
 */
export default function EnvironmentDetail({ environmentId }) {
	const { config } = React.useContext(ConfigContext);
	const api = useApi();

	const [values, setValues] = React.useState({
		config,
		environmentAddModalIsOpen: false,
		environmentDeleteModalIsOpen: false,
		applicationURIAddModalIsOpen: false,
		applicationURIDeleteModalIsOpen: false,
		acceptedDomainAddModalIsOpen: false,
		acceptedDomainDeleteModalIsOpen: false,
		_id: environmentId,
		name: null,
		integrations: null,
		domains: [],
		uri: {},
		applicationId: null,
		tenantId: null,
		appId: null,
		createdAt: null,
		createdBy: null,
		updatedAt: null,
		updatedBy: null,
		deletedAt: null,
		deletedBy: null,
		deleted: false,
		loading: true
	});

	React.useEffect(() => {
		(async () => {
			const loadedValues = await onLoad({ values, api });
			setValues(loadedValues);
		})();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	/**
	 * Handles a change to a specific field value.
	 *
	 * @param {*} newVal - The new value for the field.
	 * @param {string} path - The path of the field in the values object.
	 */
	const onChange = (newVal, path) => {
		setValues(prev => {
			const updated = { ...prev };
			set(updated, path, newVal);
			return updated;
		});
	};

	/**
	 * Handles a bulk update of form values.
	 *
	 * @param {Object} newValues - An object containing new key/value pairs for the form.
	 */
	const onChangeBulk = (newValues) => {
		setValues(prev => ({ ...prev, ...newValues }));
	};

	/**
	 * Handler for invalid form submissions.
	 */
	const onInvalidForm = () => {};

	return (
		<Box>
			{values.loading && (
				<Container>
					Loading...
				</Container>
			)}
			{!values.loading && (
				<Box sx={{ p: 0 }}>
					<EnvironmentURIAddModal
						onChange={onChange}
						onChangeBulk={onChangeBulk}
						onInvalidForm={onInvalidForm}
						config={config}
						isOpen={values.applicationURIAddModalIsOpen}
						environmentId={values._id}
					/>
					<EnvironmentDomainAddModal
						onChange={onChange}
						onChangeBulk={onChangeBulk}
						onInvalidForm={onInvalidForm}
						config={config}
						isOpen={values.acceptedDomainAddModalIsOpen}
						environmentId={values._id}
					/>

					<Card sx={{ marginTop: "20px", minWidth: 252 }}>
						<CardContent>
							<Typography variant="h5" component={"div"}>
								Name
							</Typography>
							<TextField
								label="Environment Name"
								value={values.name || ""}
								onChange={(e) => {
									onChange(e.target.value, "name");
									saveField({ name: e.target.value }, api, values._id);
								}}
								fullWidth
							/>
						</CardContent>
					</Card>

					<Card sx={{ marginTop: "20px", minWidth: 252 }}>
						<CardContent>
							<Box sx={{ marginTop: "20px" }}>
								<Stack direction={"row"} alignItems={"center"}>
									<Typography variant="h5" component={"div"}>
										Application URIs
									</Typography>
									<IconButton
										onClick={() => {
											onChange(true, "applicationURIAddModalIsOpen");
										}}
									>
										<Add />
									</IconButton>
								</Stack>
								<Typography>
									Values that developer(s) will use in code to tie API URLs to features
								</Typography>

								<Stack>
									{values.uri &&
										Object.keys(values.uri).map((uriKey, uriIndex) => {
											return (
												<EnhancedTable
													container
													spacing={2}
													key={uriIndex}
													alignItems="center"
													sx={{ width: "100%" }}
												>
													<EnhancedTable item xs={11}>
														<TextField
															variant="outlined"
															label={`${capitalize(uriKey)} API`}
															value={values.uri[uriKey] || ""}
															onChange={(e) => {
																onChange(e.target.value, `uri.${uriKey}`);
																saveField({ [`uri.${uriKey}`]: e.target.value }, api, values._id);
															}}
															fullWidth
														/>
													</EnhancedTable>
													<EnhancedTable item xs={1}>
														<IconButton
															onClick={() => onChange(true, "applicationURIDeleteModalIsOpen")}
														>
															<Delete />
														</IconButton>
													</EnhancedTable>

													<EnvironmentURIDeleteModal
														onChange={onChange}
														onChangeBulk={onChangeBulk}
														onInvalidForm={onInvalidForm}
														config={config}
														isOpen={values.applicationURIDeleteModalIsOpen}
														applicationURIName={uriKey}
														environmentId={values._id}
													/>
												</EnhancedTable>
											);
										})}
								</Stack>
							</Box>
						</CardContent>
					</Card>
					<Card sx={{ marginTop: "20px", minWidth: 252 }}>
						<CardContent>
							<Box sx={{ marginTop: "20px" }}>
								<Stack direction={"row"} alignItems={"center"}>
									<Typography variant="h5" component={"div"}>
										Accepted Domains
									</Typography>
									<IconButton
										onClick={() => {
											onChange(true, "acceptedDomainAddModalIsOpen");
										}}
									>
										<Add />
									</IconButton>
								</Stack>
								<Typography>
									COR(s) field controls how an environment is matched with a config
								</Typography>

								<Stack direction={"row"} justifyContent={"space-between"} flexWrap={"wrap"}>
									{values.domains &&
										values.domains.map((domain, domainIndex) => {
											return (
												<EnhancedTable
													container
													spacing={2}
													key={domainIndex}
													alignItems="center"
													sx={{ width: "100%" }}
												>
													<EnhancedTable item xs={11}>
														<TextField
															label="Supported Domains"
															value={domain || ""}
															onChange={(e) => {
																onChange(e.target.value, `domains[${domainIndex}]`);
																saveField({ [`domains[${domainIndex}]`]: e.target.value }, api, values._id);
															}}
															fullWidth
														/>
													</EnhancedTable>
													<EnhancedTable item xs={1}>
														<IconButton
															onClick={() => onChange(true, "acceptedDomainDeleteModalIsOpen")}
														>
															<Delete />
														</IconButton>
													</EnhancedTable>

													<EnvironmentDomainDeleteModal
														onChange={onChange}
														onChangeBulk={onChangeBulk}
														onInvalidForm={onInvalidForm}
														config={config}
														isOpen={values.acceptedDomainDeleteModalIsOpen}
														acceptedDomain={domain}
														environmentId={values._id}
													/>
												</EnhancedTable>
											);
										})}
								</Stack>
							</Box>
						</CardContent>
					</Card>

					<Box sx={{ marginTop: "10px" }}>
						<Stack direction={"row"} alignItems={"center"}>
							<Typography variant="h5" component={"div"}>
								Integrations
							</Typography>
							<IconButton onClick={() => {}}>
								<Add />
							</IconButton>
						</Stack>

						<Stack direction={"column"} justifyContent={"space-between"} flexWrap={"wrap"}>
							{values.integrations &&
								Object.keys(values.integrations).map((integrationKey, integrationIndex) => {
									return (
										<Card key={integrationIndex} sx={{ marginTop: "20px", minWidth: 252 }}>
											<CardContent>
												<Typography sx={{ fontSize: 14 }} color="text.secondary" gutterBottom>
													{capitalize(integrationKey.replace(/([a-z0-9])([A-Z])/g, "$1 $2"))}
												</Typography>
												<Typography>
													Enabled
													<Switch
														checked={values.integrations[integrationKey].enabled}
														onChange={(event) => {
															const path = `integrations[${integrationKey}].enabled`;
															onChange(event.target.checked, path);
															saveField({ [path]: event.target.checked }, api, values._id);
														}}
													/>
												</Typography>
												<Box sx={{ marginTop: "20px" }}>
													<Typography>Engine</Typography>
													<FormControl sx={{ marginTop: "10px" }}>
														<InputLabel id="demo-simple-select-label">Engine</InputLabel>
														<Select
															labelId="demo-simple-select-label"
															id="demo-simple-select"
															value={values.integrations[integrationKey].engine}
															label="Engine"
														>
															{Object.keys(
																values.integrations[integrationKey].engineConfig
															).map((engineKey, engineIndex) => {
																return (
																	<MenuItem key={engineIndex} value={engineKey}>
																		{engineKey}
																	</MenuItem>
																);
															})}
														</Select>
													</FormControl>
													<EnhancedTable
														container
														spacing={2}
														key={integrationKey}
														flexWrap="wrap"
													>
														{Object.keys(
															values.integrations[integrationKey].engineConfig[
																values.integrations[integrationKey].engine
															]
														)
															.sort((a, b) => {
																const engine = values.integrations[integrationKey].engine;
																const typeA = typeof values.integrations[integrationKey].engineConfig[engine][a];
																const typeB = typeof values.integrations[integrationKey].engineConfig[engine][b];

																if (typeA === "string" && typeB !== "string") return -1;
																if (typeA !== "string" && typeB === "string") return 1;
																if (typeA === "object" && typeB !== "object") return 1;
																if (typeA !== "object" && typeB === "object") return -1;
																return 0;
															})
															.map((engineConfigKey, engineConfigIndex) => {
																const engine = values.integrations[integrationKey].engine;
																const val =
																	values.integrations[integrationKey].engineConfig[engine][engineConfigKey];
																const valType = typeof val;
																const valIsArray = Array.isArray(val);

																return (
																	<React.Fragment key={engineConfigIndex}>
																		{valType === "string" && (
																			<EnhancedTable item xs={12}>
																				<TextField
																					label={engineConfigKey}
																					value={val || ""}
																					onChange={(e) => {
																						const path = `integrations[${integrationKey}].engineConfig[${engine}][${engineConfigKey}]`;
																						onChange(e.target.value, path);
																						saveField({ [path]: e.target.value }, api, values._id);
																					}}
																					fullWidth
																				/>
																			</EnhancedTable>
																		)}
																		{!valIsArray && valType === "object" && (
																			<EnhancedTable item xs={12}>
																				<Box
																					sx={{
																						border: "1px solid #ccc",
																						borderRadius: "4px",
																						padding: "16px"
																					}}
																				>
																					<EnhancedTable container spacing={2} alignItems="center">
																						{Object.keys(val)
																							.sort((a, b) => {
																								if (a.toLowerCase() === "enabled") return -1;
																								if (b.toLowerCase() === "enabled") return 1;
																								return 0;
																							})
																							.map((nestedEngineObjectKey, nestedEngineObjectIndex) => {
																								const nestedEngineKeyVal =
																									values.integrations[integrationKey].engineConfig[engine][engineConfigKey][nestedEngineObjectKey];
																								const nestedEngineValType = typeof nestedEngineKeyVal;
																								return (
																									<React.Fragment key={nestedEngineObjectIndex}>
																										<EnhancedTable item xs={nestedEngineValType === "boolean" ? 1 : 11}>
																											{nestedEngineValType === "boolean" && (
																												<Box
																													display="flex"
																													alignItems="center"
																													justifyContent="space-between"
																													width="100%"
																												>
																													<Typography>{engineConfigKey}</Typography>
																													<Switch
																														checked={
																															values.integrations[integrationKey].engineConfig[engine][engineConfigKey][
																																nestedEngineObjectKey
																															]
																														}
																														onChange={(event) => {
																															const path = `integrations[${integrationKey}].engineConfig[${engine}][${engineConfigKey}][${nestedEngineObjectKey}]`;
																															onChange(event.target.checked, path);
																															saveField(
																																{ [path]: event.target.checked },
																																api,
																																values._id
																															);
																														}}
																													/>
																												</Box>
																											)}
																										</EnhancedTable>
																										{nestedEngineValType === "string" && (
																											<EnhancedTable item xs={nestedEngineValType === "boolean" ? 11 : 12}>
																												<TextField
																													label={nestedEngineObjectKey}
																													value={nestedEngineKeyVal || ""}
																													onChange={(e) => {
																														const path = `integrations[${integrationKey}].engineConfig[${engine}][${engineConfigKey}][${nestedEngineObjectKey}]`;
																														onChange(e.target.value, path);
																														saveField({ [path]: e.target.value }, api, values._id);
																													}}
																													fullWidth
																												/>
																											</EnhancedTable>
																										)}
																									</React.Fragment>
																								);
																							})}
																					</EnhancedTable>
																				</Box>
																			</EnhancedTable>
																		)}
																		{valIsArray &&
																			val.map((item, itemIndex) => (
																				<EnhancedTable item xs={12} key={itemIndex}>
																					<Typography>{engineConfigKey}</Typography>
																					<EnhancedTable container spacing={2}>
																						{Object.keys(item).map((itemKey) => (
																							<EnhancedTable item xs={12} key={itemKey}>
																								<TextField
																									label={itemKey}
																									value={item[itemKey] || ""}
																									onChange={(e) => {
																										const path = `integrations[${integrationKey}].engineConfig[${engine}][${engineConfigKey}][${itemIndex}][${itemKey}]`;
																										onChange(e.target.value, path);
																										saveField({ [path]: e.target.value }, api, values._id);
																									}}
																									fullWidth
																								/>
																							</EnhancedTable>
																						))}
																					</EnhancedTable>
																				</EnhancedTable>
																			))}
																	</React.Fragment>
																);
															})}
													</EnhancedTable>
												</Box>
											</CardContent>
											<CardActions></CardActions>
										</Card>
									);
								})}
						</Stack>
					</Box>
					<Button
						variant="contained"
						onClick={() => onChange(true, "environmentDeleteModalIsOpen")}
					>
						DELETE <Delete />
					</Button>
					<EnvironmentDeleteModal
						onChange={onChange}
						onChangeBulk={onChangeBulk}
						onInvalidForm={onInvalidForm}
						config={config}
						isOpen={values.environmentDeleteModalIsOpen}
						environmentId={values._id}
						environmentName={values.name}
						applicationId={values.applicationId}
					/>
				</Box>
			)}
		</Box>
	);
}

EnvironmentDetail.propTypes = {
	environmentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
};
